/**
 * Motor del día de Fuerza (WorkoutEngine.swift): máquina de estados explícita, reloj con marcas de tiempo y guardado
 * progresivo de la sesión en el libro. La interfaz se pinta solo a partir de `phase`.
 *
 *   idle → warmup → floor → offer (forma del día) → readiness → countdown → normalActive → celebration → feedback → finished
 *   Día de solo suelo: readiness → celebration → feedback → doneFloorOnly (y aún se puede seguir con el normal).
 *
 * La verdad es la sesión de hoy en el libro. Lo que el libro no guarda (fase intermedia, calentamiento en marcha,
 * pausas, paso de la ronda guiada) vive en localStorage `sut-engine`; si se pierde (otro dispositivo, datos
 * borrados) la fase se deduce de la sesión, igual que `hydrateToday()` en iOS.
 */
import {
  dateKey, decodeFeedback, encodeFeedback, frozenDurationSeconds, isCeilingReached, routineFromSettings, routineSnapshot,
  wasPerformed, workoutConfig, type ClockState, type Exercise, type MeasurementCode, type WorkoutConfig,
} from '@sut/domain';
import { newId, type Session, type Workbook } from '@sut/workbook';

export type Phase =
  | 'idle' | 'warmup' | 'floor' | 'offer' | 'readiness' | 'countdown' | 'normalActive'
  | 'celebration' | 'feedback' | 'doneFloorOnly' | 'finished';

interface Ephemeral {
  date: string;
  sessionId: string;
  phase: Phase;
  warmupStartedAt: number | null;
  warmupSeconds: number | null;
  pausedAt: number | null;
  pauseMs: number;
  step: number;
  measureLastRound: boolean;
}

export interface EngineDeps {
  workbook(): Workbook | null;
  /** Guarda (en cola) la sesión completa: upsert idempotente por id. */
  saveSession(session: Session, label: string): void;
  setSetting(key: string, value: string): void;
  measureLastRound(): boolean;
  countdownEnabled(): boolean;
}

export interface FeedbackInput { effort: number; activity: string; pain: boolean; painNote: string; note: string }

const KEY = 'sut-engine';

function emptySession(id: string, date: string): Session {
  return {
    id, date, floorCompletedAt: '', normalStartedAt: '', normalFinishedAt: '', sessionType: 'FLOOR', rounds: 0,
    durationSeconds: 0, effort: null, activity: '', pain: null, painNote: '', note: '', nextDaySoreness: null,
    wantToReturn: null, recoveryLoggedAt: '', exerciseFeedback: '', routineSnapshot: '', warmupSeconds: null,
  };
}

export class Engine {
  phase: Phase = 'idle';
  session: Session | null = null;
  /** Instante de la última comprobación (lo refresca `tick`). */
  now = Date.now();
  /** El techo saltó al terminar (para la tarjeta final; el libro lo deduce de la duración). */
  maxTimeReached = false;
  private e: Ephemeral = this.blank();

  constructor(private deps: EngineDeps) {}

  private blank(): Ephemeral {
    return { date: dateKey(), sessionId: '', phase: 'idle', warmupStartedAt: null, warmupSeconds: null, pausedAt: null, pauseMs: 0, step: 0, measureLastRound: true };
  }

  // -------------------------------------------------------------------------------------------------------------
  // Configuración (rutina de la pestaña Rutina o, si aún no existe, la de Settings)

  get config(): WorkoutConfig {
    const wb = this.deps.workbook();
    const settings = wb?.settings ?? {};
    return workoutConfig(settings, wb?.routine ?? routineFromSettings(settings));
  }

  // -------------------------------------------------------------------------------------------------------------
  // Hidratación: dónde está el día según el libro (y lo efímero, si cuadra)

  hydrate(): void {
    const today = dateKey();
    let stored: Ephemeral | null = null;
    try { stored = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Ephemeral | null; } catch { stored = null; }
    if (stored?.date !== today) stored = null;

    const wb = this.deps.workbook();
    const todays = (wb?.sessions ?? []).filter(s => s.date === today);
    const s = todays.find(x => x.id === stored?.sessionId) ?? todays[todays.length - 1] ?? null;
    this.session = s;
    this.e = stored ? { ...stored, sessionId: s?.id ?? stored.sessionId } : { ...this.blank(), sessionId: s?.id ?? '' };
    const want = stored?.phase ?? 'idle';
    this.phase = this.derive(s, want);
    if (this.phase === 'normalActive') {
      const answers = decodeFeedback(s?.exerciseFeedback ?? '');
      if (!stored) this.e.measureLastRound = Object.keys(answers).length > 0 || this.deps.measureLastRound();
      this.e.step = this.firstUnanswered(answers) ?? Math.max(0, this.config.normal.length - 1);
    }
    if (this.phase !== 'normalActive') { this.e.pausedAt = null; this.e.pauseMs = 0; }
    this.maxTimeReached = !!s?.normalFinishedAt && s.durationSeconds >= this.config.ceilingSeconds;
    this.e.phase = this.phase;
    this.persist();
  }

  private derive(s: Session | null, want: Phase): Phase {
    if (!s) return want === 'warmup' || want === 'floor' ? want : 'idle';
    if (s.normalStartedAt && !s.normalFinishedAt) return 'normalActive';
    if (s.effort !== null) {
      if (s.normalFinishedAt && s.normalStartedAt) return want === 'celebration' || want === 'feedback' ? want : 'finished';
      return want === 'countdown' ? 'countdown' : 'doneFloorOnly';
    }
    if (s.normalFinishedAt) return want === 'celebration' ? 'celebration' : 'feedback';
    if (s.floorCompletedAt) return (['offer', 'readiness', 'countdown', 'celebration', 'feedback'] as Phase[]).includes(want) ? want : 'readiness';
    return want === 'warmup' || want === 'floor' ? want : 'idle';
  }

  private persist() {
    try { localStorage.setItem(KEY, JSON.stringify({ ...this.e, phase: this.phase })); } catch { /* sin almacenamiento */ }
  }

  private go(phase: Phase) { this.phase = phase; this.e.phase = phase; this.persist(); }

  private save(patch: Partial<Session>, label: string) {
    const base = this.session ?? emptySession(this.e.sessionId || newId(), dateKey());
    this.session = { ...base, ...patch };
    this.e.sessionId = this.session.id;
    this.deps.saveSession(this.session, label);
  }

  // -------------------------------------------------------------------------------------------------------------
  // Derivados

  /** Hoy ya está el mínimo (el suelo): es lo que quita el globo del icono. */
  get trainedToday(): boolean { return !!this.session?.floorCompletedAt && this.session.date === dateKey(); }

  get clock(): ClockState | null {
    const start = this.session?.normalStartedAt;
    if (!start) return null;
    return { startedAt: Date.parse(start), ceilingSeconds: this.config.ceilingSeconds, accumulatedPauseMs: this.e.pauseMs, pausedAt: this.e.pausedAt };
  }

  get isPaused(): boolean { return this.e.pausedAt !== null; }
  get roundsCompleted(): number { return this.session?.rounds ?? 0; }
  get currentRound(): number { return Math.min(this.roundsCompleted + 1, this.config.maxRounds); }
  get answers(): Record<string, MeasurementCode> { return decodeFeedback(this.session?.exerciseFeedback ?? ''); }

  get warmupRunning(): boolean { return this.e.warmupStartedAt !== null; }
  warmupElapsed(now = Date.now()): number { return this.e.warmupStartedAt ? Math.max(0, Math.floor((now - this.e.warmupStartedAt) / 1000)) : 0; }

  /** La última ronda va ejercicio a ejercicio con la pregunta de RIR (las anteriores, con la lista de siempre). */
  get isGuidedRound(): boolean {
    const c = this.config;
    return this.phase === 'normalActive' && this.e.measureLastRound && c.maxRounds >= 2 && this.roundsCompleted === c.maxRounds - 1 && c.normal.length > 0;
  }
  get step(): number { return this.e.step; }
  get guidedExercise(): Exercise | null { return this.isGuidedRound ? this.config.normal[this.e.step] ?? null : null; }
  get previousExercise(): Exercise | null { return this.isGuidedRound && this.e.step > 0 ? this.config.normal[this.e.step - 1] ?? null : null; }
  get nextExercise(): Exercise | null { return this.isGuidedRound ? this.config.normal[this.e.step + 1] ?? null : null; }

  private firstUnanswered(answers: Record<string, MeasurementCode>): number | null {
    const i = this.config.normal.findIndex(e => !(e.id in answers));
    return i < 0 ? null : i;
  }

  // -------------------------------------------------------------------------------------------------------------
  // Transiciones

  beginDay() {
    if (this.phase !== 'idle') return;
    this.e = { ...this.blank(), sessionId: newId() };
    this.go('warmup');
  }

  startWarmup() {
    if (this.phase !== 'warmup' || this.e.warmupStartedAt) return;
    this.e.warmupStartedAt = Date.now();
    this.persist();
  }

  finishWarmup() {
    if (this.phase !== 'warmup') return;
    this.e.warmupSeconds = this.e.warmupStartedAt ? this.warmupElapsed() : null;
    this.e.warmupStartedAt = null;
    this.go('floor');
  }

  skipWarmup() {
    if (this.phase !== 'warmup') return;
    this.e.warmupStartedAt = null;
    this.e.warmupSeconds = null;
    this.go('floor');
  }

  completeFloor() {
    if (this.phase !== 'floor' && this.phase !== 'idle') return;
    const now = new Date().toISOString();
    const settings = this.deps.workbook()?.settings ?? {};
    // Inicio del programa en el primer suelo (START_DATE de la web)
    if (!settings.START_DATE) this.deps.setSetting('START_DATE', dateKey());
    this.save({ floorCompletedAt: now, warmupSeconds: this.e.warmupSeconds, routineSnapshot: routineSnapshot(this.config) }, 'Suelo hecho');
    this.go('offer');
  }

  /** Tras la forma del día («Empezar forma» o «Ahora no»). */
  proceedFromOffer() { if (this.phase === 'offer') this.go('readiness'); }

  finishFloorOnly() {
    if (this.phase !== 'readiness') return;
    this.save({ sessionType: 'FLOOR', durationSeconds: 0, normalStartedAt: '', normalFinishedAt: '' }, 'Día de suelo');
    this.go('celebration');
  }

  /** «Estoy verde → seguir», o más tarde desde un día de solo suelo. */
  startNormal() {
    if (this.phase !== 'readiness' && this.phase !== 'doneFloorOnly') return;
    if (this.deps.countdownEnabled()) this.go('countdown');
    else { this.phase = 'countdown'; this.beginRounds(); }
  }

  beginRounds() {
    if (this.phase !== 'countdown') return;
    this.e.pausedAt = null;
    this.e.pauseMs = 0;
    this.e.step = 0;
    this.e.measureLastRound = this.deps.measureLastRound();
    this.maxTimeReached = false;
    this.save({
      normalStartedAt: new Date().toISOString(), normalFinishedAt: '', sessionType: 'NORMAL', rounds: 0, durationSeconds: 0,
      exerciseFeedback: '', routineSnapshot: routineSnapshot(this.config),
    }, 'Entreno normal empezado');
    this.go('normalActive');
  }

  /** @return 'next' (sigue otra ronda), 'done' (terminó), 'ceiling' (saltó el techo) o null si no aplica. */
  completeRound(): 'next' | 'done' | 'ceiling' | null {
    const clock = this.clock;
    if (this.phase !== 'normalActive' || this.isGuidedRound || !clock || this.isPaused) return null;
    if (isCeilingReached(clock, Date.now())) { this.endNormal(true); return 'ceiling'; }
    const rounds = this.roundsCompleted + 1;
    this.save({ rounds }, `Ronda ${rounds}`);
    this.e.step = 0;
    if (rounds >= this.config.maxRounds) { this.endNormal(false); return 'done'; }
    this.persist();
    return 'next';
  }

  /** Respuesta del ejercicio en pantalla (un toque = hecho + valorado; «skipped» = no hecho). */
  recordAnswer(code: MeasurementCode): 'next' | 'done' | 'ceiling' | null {
    const ex = this.guidedExercise;
    const clock = this.clock;
    if (!ex || !clock || this.isPaused) return null;
    const answers = { ...this.answers, [ex.id]: code };
    const order = this.config.normal.map(e => e.id);
    this.save({ exerciseFeedback: encodeFeedback(answers, order) }, `RIR ${ex.name}`);
    if (isCeilingReached(clock, Date.now())) { this.endNormal(true); return 'ceiling'; }
    const next = this.firstUnanswered(answers);
    if (next !== null) { this.e.step = next; this.persist(); return 'next'; }
    // Fin de la ronda guiada: cuenta como ronda completa solo si se hicieron todos los ejercicios
    const all = this.config.normal.every(e => wasPerformed(answers[e.id]));
    this.endNormal(false, all ? this.roundsCompleted + 1 : this.roundsCompleted);
    return 'done';
  }

  revisePrevious() {
    if (!this.previousExercise) return;
    this.e.step -= 1;
    this.persist();
  }

  finishEarly() { if (this.phase === 'normalActive') this.endNormal(false); }

  pause() {
    if (this.phase !== 'normalActive' || this.isPaused) return;
    this.e.pausedAt = Date.now();
    this.persist();
  }

  resume() {
    if (this.phase !== 'normalActive' || this.e.pausedAt === null) return;
    this.e.pauseMs += Date.now() - this.e.pausedAt;
    this.e.pausedAt = null;
    this.persist();
  }

  /** Cada pocos ms mientras hay reloj. @return true si saltó el techo (cambia la fase). */
  tick(): boolean {
    this.now = Date.now();
    const clock = this.clock;
    if (this.phase === 'normalActive' && clock && !this.isPaused && isCeilingReached(clock, this.now)) {
      this.endNormal(true);
      return true;
    }
    return false;
  }

  private endNormal(ceiling: boolean, rounds = this.roundsCompleted) {
    const clock = this.clock;
    const end = Date.now();
    this.maxTimeReached = ceiling;
    this.save({
      normalFinishedAt: new Date(end).toISOString(), rounds, sessionType: 'NORMAL',
      durationSeconds: clock ? frozenDurationSeconds(clock, end) : 0,
    }, 'Entreno terminado');
    this.e.pausedAt = null;
    this.go('celebration');
  }

  proceedToFeedback() { if (this.phase === 'celebration') this.go('feedback'); }

  submitFeedback(f: FeedbackInput) {
    if (this.phase !== 'feedback') return;
    this.save({
      effort: Math.min(10, Math.max(1, Math.round(f.effort))), activity: f.activity, pain: f.pain,
      painNote: f.pain ? f.painNote.trim() : '', note: f.note.trim(),
    }, 'Cuestionario del día');
    this.go(this.session?.normalStartedAt ? 'finished' : 'doneFloorOnly');
  }

  /** ¿Pantalla encendida? (calentamiento en marcha, cuenta atrás, rondas, celebración y cuestionario, como en iOS). */
  get wantsAwake(): boolean {
    return (this.phase === 'warmup' && this.warmupRunning) || (['countdown', 'normalActive', 'celebration'] as Phase[]).includes(this.phase)
      || (this.phase === 'feedback' && !!this.session?.normalStartedAt);
  }

  /** ¿Hace falta el temporizador de pantalla? */
  get ticking(): boolean { return (this.phase === 'warmup' && this.warmupRunning) || this.phase === 'normalActive'; }
}

/**
 * Recuperación pendiente: la sesión anterior más reciente, si tiene cuestionario y aún no tiene agujetas.
 * Se pregunta después del suelo de hoy, cuando el movimiento da una lectura más fiable.
 */
export function pendingRecovery(wb: Workbook | null, today = dateKey()): Session | null {
  const prev = (wb?.sessions ?? []).filter(s => s.date && s.date < today).sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  return prev && prev.effort !== null && !prev.recoveryLoggedAt ? prev : null;
}
